import { ProgramInviteType } from 'models';
import { useCallback, useState } from 'react';
import { AxiosErrorHandler, Modal, Skeleton, message } from 'ui';
import { QuickInviteForm, type QuickInviteFormData } from './components';
import { useEventQuery, useUpdateEventInviteeRole } from './queries';

type P = {
  eventId: string;
  isOpened: boolean;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
};

export const QuickInviteEventAttendees = ({ eventId, isOpened, onCancel, onSuccess }: P) => {
  const [inviteType, setInviteType] = useState(ProgramInviteType.empty);
  const isInviteOpened = inviteType !== ProgramInviteType.empty;

  const {
    data: eventContentObject,
    error: eventError,
    isFetching: isFetchingEvent,
    isLoading: isLoadingEvent,
  } = useEventQuery({ id: eventId, options: { throwOnError: false } });

  const { isPending: isInviting, mutateAsync: inviteUsers } = useUpdateEventInviteeRole();

  const onSubmit = useCallback(
    async (data: QuickInviteFormData) => {
      const { emailInvitees = [], invitees = [] } = data.formValues;

      await inviteUsers(
        {
          emailInvitees,
          eventId,
          invitees,
        },
        {
          async onSuccess() {
            message.success('Invitations sent successfully');
            await onSuccess();
          },
        },
      );
    },
    [eventId, inviteUsers, onSuccess],
  );

  const onBackClick = () => {
    setInviteType(ProgramInviteType.empty);
  };

  const getTitleName = () => {
    if (isInviteOpened) {
      switch (inviteType) {
        case ProgramInviteType.invitees:
          return 'Invite attendees';
        case ProgramInviteType.moderators:
          return 'Invite admins / hosts';
        case ProgramInviteType.presenters:
          return 'Invite presenters';
      }
    }

    return 'Invite members';
  };

  return (
    <Modal
      closable={!isInviteOpened}
      isOpened={isOpened}
      maskClosable={false}
      onCancel={onCancel}
      title={getTitleName()}
      width="medium"
    >
      <Modal.HugContent>
        <AxiosErrorHandler actions={false} error={eventError}>
          {(isLoadingEvent && isFetchingEvent) || !eventContentObject ? (
            <Skeleton active paragraph={{ rows: 12 }} />
          ) : (
            <QuickInviteForm
              eventContentObject={eventContentObject}
              inviteType={inviteType}
              isUpdating={isInviting}
              onBack={onBackClick}
              onInviteesTypeChanged={setInviteType}
              onSubmit={onSubmit}
            />
          )}
        </AxiosErrorHandler>
      </Modal.HugContent>
    </Modal>
  );
};
